import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../helpers/axiosInstance";
import Loading from "../../shared/loading/Loading";
import { toast } from "react-toastify";

/**
 * Where a password reset link lands.
 *
 * The server has been emailing links to /reset-password?token=... for both the
 * administrator's "Reset password" button and a member's own "forgot my
 * password" — but there was no route for it, so every one of those links fell
 * through to RequireAuth and bounced to /signin with no explanation. The
 * member could never set a password.
 *
 * The token is the authorisation: somebody using this cannot sign in, which is
 * the whole point. It is checked with validateOnly before asking for anything,
 * so an expired or wrong link says so instead of taking a new password and
 * then refusing it.
 */
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function validateLink() {
      if (!token) {
        setLoading(false);
        setMessage("This link is missing its token. Ask for a new reset link.");
        return;
      }

      try {
        const response = await axiosInstance.post("/api/member/resetpassword", {
          token,
          validateOnly: true,
        });
        setValid(true);
        setEmail(response.data?.email || "");
        setMessage("Choose a new password for your account.");
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "This reset link could not be checked. Ask for a new one.";
        setMessage(errorMessage);
      } finally {
        setLoading(false);
      }
    }

    validateLink();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast("Please enter both password fields.");
      return;
    }
    if (password !== confirmPassword) {
      toast("Passwords do not match.");
      return;
    }
    // The same minimum the server enforces, so it is said before the round
    // trip rather than after.
    if (password.length < 6) {
      toast("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/api/member/resetpassword", {
        token,
        password,
      });
      toast(response.data?.message || "Password updated. Please sign in.");
      navigate("/signin", { replace: true });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to set the password. Please try again.";
      toast(errorMessage);
      setMessage(errorMessage);
      // A link that has expired mid-way through is no longer usable, so the
      // form is taken away rather than left to fail again.
      if (error?.response?.status === 400 && /expired|not a password reset/i.test(errorMessage)) {
        setValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <div className="signin-login-wrapper" style={{ maxWidth: 500, width: "100%" }}>
        <label>Set a new password</label>
        <div
          className="signin-login-formsection"
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          {loading ? (
            <Loading />
          ) : valid ? (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <p>{message}</p>
              {email ? (
                <p style={{ opacity: 0.7, fontSize: "0.9em" }}>{email}</p>
              ) : null}
              <div className="signin-login-formsection" style={{ flexDirection: "column" }}>
                <input
                  type="password"
                  value={password}
                  placeholder="New password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <input
                  type="password"
                  value={confirmPassword}
                  placeholder="Confirm new password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button className="signin-login-btn" type="submit" disabled={loading}>
                  {loading ? <Loading /> : "Set password"}
                </button>
              </div>
            </form>
          ) : (
            <div style={{ textAlign: "center" }}>
              <p>{message}</p>
              <button
                className="signin-login-btn"
                onClick={() => navigate("/signin", { replace: true })}
              >
                Go to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
